/**
 * runAssetUploadService（SEC-01 S4 落地）
 *
 * 批次上传服务：将「后端 authorization」与「R2 PUT」解耦。一次 authorization
 * 固定包含 original（必填）、可选 processed；再按后端返回状态对 required item
 * 执行 R2 PUT，最多两轮收敛（初始轮 + 1 次结果未确认后的重授权轮）。
 *
 * 职责边界（方案 §5.1 / §5.2.1）：
 * - 组织批次 authorization、校验状态、对所有 required item 执行 PUT、一次重授权收敛
 * - 不拼 key / public URL、不提交 /runs/ 或 /photos/（那是 orchestrator / API 层职责）
 * - 向 gateway 发送的 payload 只取 hashId / fileType，不传 source 或 MIME
 * - 成功返回去敏批次结果（不含 uploadUrl / requiredHeaders / expiresAt）
 * - Backend authorization 4xx/5xx 不自动重试，原样上抛
 */

import analysisGateway from '@/core/analysis/gateway/AnalysisGateway';
import objectStorageService from '@/core/upload/objectStorageService';

const MAX_AUTHORIZATION_ROUNDS = 2;

const createInvalidUploadAssetError = (detail) => {
    const error = new Error(`[runAssetUploadService] invalid_upload_asset: ${detail}`);
    error.code = 'invalid_upload_asset';
    return error;
};

const createInvalidUploadFileTypeError = (value) => {
    const error = new Error(`[runAssetUploadService] invalid_upload_file_type: ${value}`);
    error.code = 'invalid_upload_file_type';
    return error;
};

const createRunAssetUploadUnconfirmedError = (role = null) => {
    const error = new Error('[runAssetUploadService] run_asset_upload_unconfirmed');
    // 仅保留稳定 code/status/role；绝不附带 URL、headers、R2 XML body 或原始响应。
    error.code = 'run_asset_upload_unconfirmed';
    error.status = null;
    error.responseData = null;
    error.role = role;
    return error;
};

// §5.4：本地 MIME / 扩展名 → API file_type 固定枚举映射；非法类型请求前失败。
const normalizeUploadFileType = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) {
        throw createInvalidUploadFileTypeError(value);
    }
    if (normalized === 'image/jpeg' || normalized === 'jpg') {
        return 'jpg';
    }
    if (normalized === 'jpeg') {
        return 'jpeg';
    }
    if (normalized === 'image/png' || normalized === 'png') {
        return 'png';
    }
    if (normalized === 'image/webp' || normalized === 'webp') {
        return 'webp';
    }
    throw createInvalidUploadFileTypeError(value);
};

const desensitizeResult = (authorizationResult) => {
    const roles = ['original'];
    if (authorizationResult.processed) {
        roles.push('processed');
    }
    const result = {};
    roles.forEach((role) => {
        const item = authorizationResult[role];
        result[role] = {
            objectUploadRequired: item.objectUploadRequired,
            runAssetMaterialRequired: item.runAssetMaterialRequired,
            resourceKey: item.resourceKey,
            fileType: item.fileType
        };
    });
    return result;
};

export const createRunAssetUploadService = ({ authorizationGateway, storageService } = {}) => {
    if (!authorizationGateway || typeof authorizationGateway.authorizeRunAssetUploads !== 'function') {
        throw new Error('invalid_authorization_gateway');
    }
    if (!storageService || typeof storageService.putPresignedObject !== 'function') {
        throw new Error('invalid_storage_service');
    }

    // §4.1 / A.2 P2：请求前校验 normalized hash_id 符合 SoR 规则（长度 1..255、禁止禁用字符与值），
    // 避免明显非法 descriptor 穿透到 Backend 400 而非前端 fail-fast。
    // eslint-disable-next-line no-control-regex
    const FORBIDDEN_HASH_CHARS = /[/\\]/;
    const hasControlChar = (s) => {
        for (let i = 0; i < s.length; i += 1) {
            const code = s.charCodeAt(i);
            if (code <= 0x1f || code === 0x7f) {
                return true;
            }
        }
        return false;
    };
    const validateHashId = (rawValue, label) => {
        // SoR 要求 hash_id 为 string 类型；严格拒绝非字符串输入，不经由 String() 隐式转换。
        if (typeof rawValue !== 'string') {
            throw createInvalidUploadAssetError(`${label} hash_id must be a string`);
        }
        const trimmed = rawValue.trim();
        if (!trimmed) {
            throw createInvalidUploadAssetError(`${label} hash_id must not be empty after trim`);
        }
        // 按 Unicode code point 计数（emoji 等非 BMP 字符在 UTF-16 下占 2 个 code unit），与后端一致。
        if ([...trimmed].length > 255) {
            throw createInvalidUploadAssetError(`${label} hash_id exceeds maximum length of 255`);
        }
        if (FORBIDDEN_HASH_CHARS.test(trimmed) || hasControlChar(trimmed)) {
            throw createInvalidUploadAssetError(`${label} hash_id contains forbidden characters`);
        }
        if (trimmed === '.' || trimmed === '..') {
            throw createInvalidUploadAssetError(`${label} hash_id must not be "." or ".."`);
        }
        return trimmed;
    };

    // §5.2.1 / A.2 P2：PUT 前校验 source 类型为合法上传体（File / Blob / data URL）；
    // 确定性客户端输入错误应直接上抛为 invalid_upload_asset，不得被误判为 R2 未确认。
    const validateSource = (source, role) => {
        if (!source) {
            return; // 缺失由后续 truthy 检查处理
        }
        if (source instanceof File || source instanceof Blob) {
            return;
        }
        if (typeof source === 'string' && source.startsWith('data:')) {
            return;
        }
        throw createInvalidUploadAssetError(
            `source for role "${role}" must be File, Blob, or data URL string`
        );
    };

    const buildAuthorizationPayload = ({ original, processed }) => {
        const payload = {
            original: {
                hash_id: validateHashId(original.hashId, 'original'),
                file_type: normalizeUploadFileType(original.fileType)
            }
        };
        if (processed) {
            payload.processed = {
                hash_id: validateHashId(processed.hashId, 'processed'),
                file_type: normalizeUploadFileType(processed.fileType)
            };
        }
        return payload;
    };

    // 仅允许进入重授权/未确认路径的错误码（§5.2.1）；
    // 其余确定性错误（如 invalid_upload_source / presigned_upload_invalid_header）
    // 直接上抛，不浪费 Backend authorization 重试机会。
    const RETRYABLE_UPLOAD_CODES = new Set([
        'presigned_upload_failed',
        'presigned_upload_transport_failed'
    ]);

    const authorizeAndUploadAssets = async ({ original, processed } = {}) => {
        if (!original || !original.hashId || !original.fileType) {
            throw createInvalidUploadAssetError('original descriptor (hashId, fileType) is required');
        }
        if (processed && (!processed.hashId || !processed.fileType)) {
            throw createInvalidUploadAssetError('processed descriptor (hashId, fileType) is required when present');
        }

        // §4.1：original 与 processed 的 normalized hash_id 必须不同；请求前 fail-fast 避免生成非法 payload。
        if (processed && original.hashId && processed.hashId
            && String(original.hashId).trim() === String(processed.hashId).trim()) {
            throw createInvalidUploadAssetError('original and processed must use distinct hash_id');
        }

        const payload = buildAuthorizationPayload({ original, processed });
        const roles = ['original'];
        if (processed) {
            roles.push('processed');
        }

        let lastFailedRole = null;
        for (let round = 0; round < MAX_AUTHORIZATION_ROUNDS; round += 1) {
            // Backend authorization 4xx/5xx：不计入 R2 未确认，不在服务内自动重试，原样上抛。
            const authorizationResult = await authorizationGateway.authorizeRunAssetUploads(payload);

            const requiredRoles = roles.filter(
                (role) => authorizationResult[role]?.objectUploadRequired === true
            );

            // 只有 required item 才需要对应 source 存在且类型合法（A.2 P2 source 分类修复）。
            for (const role of requiredRoles) {
                const source = role === 'original' ? original.source : processed.source;
                if (!source) {
                    throw createInvalidUploadAssetError(`missing source for required role "${role}"`);
                }
                validateSource(source, role);
            }

            // PUT 前检查 expiresAt；已到期视为本轮未确认，不发送旧 URL。
            const now = Date.now();
            const expiredRole = requiredRoles.find((role) => {
                const expiresAt = authorizationResult[role].expiresAt;
                if (!expiresAt) {
                    return false;
                }
                const expiry = Date.parse(expiresAt);
                return Number.isNaN(expiry) || expiry <= now;
            });
            if (expiredRole) {
                // 进入下一轮 authorization（以最新状态重新收敛）；无剩余轮次则循环结束抛未确认。
                lastFailedRole = expiredRole;
                // eslint-disable-next-line no-continue
                continue;
            }

            // 按 original、processed 固定顺序处理所有 required item。
            let settled = true;
            for (const role of requiredRoles) {
                const item = authorizationResult[role];
                const source = role === 'original' ? original.source : processed.source;
                try {
                    await storageService.putPresignedObject({
                        source,
                        uploadUrl: item.uploadUrl,
                        requiredHeaders: item.requiredHeaders
                    });
                } catch (putError) {
                    // 仅 presigned_upload_failed / presigned_upload_transport_failed 触发重授权；
                    // 其余（含无 code 的原生异常、invalid_upload_source / presigned_upload_invalid_header 等）
                    // 一律直接上抛，不误判为 R2 未确认、不浪费 Backend authorization 重试。
                    if (!RETRYABLE_UPLOAD_CODES.has(putError?.code)) {
                        throw putError;
                    }
                    settled = false;
                    lastFailedRole = role;
                    break;
                }
            }

            if (settled) {
                return desensitizeResult(authorizationResult);
            }
            // 本轮未确认：进入下一轮 authorization。
        }

        throw createRunAssetUploadUnconfirmedError(lastFailedRole);
    };

    return {
        authorizeAndUploadAssets,
        normalizeUploadFileType
    };
};

// 默认单例：apiService 与 ResultView 复用该实例，禁止各调用方自行创建不同协议实现。
export const runAssetUploadService = createRunAssetUploadService({
    authorizationGateway: analysisGateway,
    storageService: objectStorageService
});

export {
    normalizeUploadFileType
};

export default runAssetUploadService;
